import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Fuel, CreditCard, Edit, Trash2, Wrench, User } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const CARD_TYPES = [
  { value: 'carburante', label: 'Carta Carburante', icon: Fuel, color: 'bg-orange-50 text-orange-700 border-orange-200' },
  { value: 'telepass', label: 'Telepass', icon: CreditCard, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'prepagata', label: 'Carta Prepagata', icon: CreditCard, color: 'bg-purple-50 text-purple-700 border-purple-200' },
];

const emptyForm = {
  type: 'carburante', provider: '', card_number: '',
  assigned_type: '__none__', assigned_id: '',
  responsible_id: '__none__',
  plafond: '', expiry_date: '', is_active: true, notes: '',
};

export default function PaymentCardsPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'qhse');
  const [cards, setCards] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('__all__');
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterType !== '__all__') params.type = filterType;
      const [cardsRes, equipRes, empRes] = await Promise.all([
        api.get('/payment-cards', { params }),
        api.get('/equipment').catch(() => ({ data: [] })),
        api.get('/employees').catch(() => ({ data: [] })),
      ]);
      setCards(cardsRes.data);
      setEquipment(equipRes.data || []);
      setEmployees((empRes.data || []).filter((e) => e.is_active));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditItem(null); setForm({ ...emptyForm }); setShowDialog(true); };
  const openEdit = (c) => {
    setEditItem(c);
    setForm({
      ...emptyForm, ...c,
      assigned_type: c.assigned_type || '__none__',
      assigned_id: c.assigned_id || '',
      responsible_id: c.responsible_id || '__none__',
      plafond: c.plafond ?? '',
      expiry_date: c.expiry_date || '',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        assigned_type: form.assigned_type === '__none__' ? null : form.assigned_type,
        assigned_id: form.assigned_type === '__none__' ? null : (form.assigned_id || null),
        responsible_id: form.responsible_id === '__none__' ? null : form.responsible_id,
        plafond: form.plafond === '' ? null : parseFloat(form.plafond),
      };
      if (editItem) {
        await api.put(`/payment-cards/${editItem.id}`, payload);
      } else {
        await api.post('/payment-cards', payload);
      }
      setShowDialog(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa carta?')) return;
    try { await api.delete(`/payment-cards/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const typeInfo = (t) => CARD_TYPES.find((c) => c.value === t) || CARD_TYPES[0];

  return (
    <div className="space-y-6" data-testid="payment-cards-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Carte Carburante &amp; Telepass</h1>
          <p className="text-sm text-slate-500 mt-1">Carte carburante, Telepass e prepagate assegnate a mezzi o persone, con plafond</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="add-card-btn"><Plus size={16} className="mr-1" /> Nuova Carta</Button>
        )}
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {CARD_TYPES.map((t) => {
          const list = cards.filter((c) => c.type === t.value);
          const count = list.length;
          const totalPlafond = list.reduce((sum, c) => sum + (c.plafond || 0), 0);
          const Icon = t.icon;
          return (
            <Card key={t.value} className="border-slate-200 shadow-sm" data-testid={`kpi-${t.value}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                  <Icon size={18} className="text-slate-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-slate-500">{t.label}</p>
                  <p className="text-xl font-bold text-slate-700">{count}</p>
                  {t.value !== 'telepass' && totalPlafond > 0 && <p className="text-[11px] text-slate-400">plafond tot. {totalPlafond.toFixed(2)} EUR</p>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtro tipo */}
      <div className="flex flex-wrap gap-2">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-56" data-testid="card-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tutti i tipi</SelectItem>
            {CARD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tabella */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Tipo</TableHead>
                <TableHead>Fornitore</TableHead>
                <TableHead>Numero carta</TableHead>
                <TableHead>Assegnata a</TableHead>
                <TableHead>Responsabile</TableHead>
                <TableHead className="text-right">Plafond</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Note</TableHead>
                {canManage && <TableHead className="w-24">Azioni</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
              ) : cards.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Nessuna carta registrata</TableCell></TableRow>
              ) : cards.map((c) => {
                const info = typeInfo(c.type);
                return (
                  <TableRow key={c.id} className="hover:bg-slate-50/50" data-testid={`card-row-${c.id}`}>
                    <TableCell><Badge variant="outline" className={`text-[10px] ${info.color}`}>{info.label}</Badge></TableCell>
                    <TableCell className="text-sm font-medium">{c.provider || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600 font-mono">{c.card_number || '-'}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {c.assigned_name ? (
                        <span className="inline-flex items-center gap-1">
                          {c.assigned_type === 'equipment' ? <Wrench size={12} className="text-slate-400" /> : <User size={12} className="text-slate-400" />}
                          {c.assigned_name}
                        </span>
                      ) : <span className="text-slate-300">Non assegnata</span>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {c.responsible_name ? <span className="inline-flex items-center gap-1"><User size={12} className="text-slate-400" />{c.responsible_name}</span> : <span className="text-slate-300">-</span>}
                    </TableCell>
                    <TableCell className="text-sm text-right">{c.plafond != null ? `${Number(c.plafond).toFixed(2)} EUR` : '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={c.is_active ? 'bg-green-50 text-green-700 border-green-200 text-[10px]' : 'bg-slate-100 text-slate-400 border-slate-200 text-[10px]'}>
                        {c.is_active ? 'Attiva' : 'Disattiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[160px] truncate">{c.notes || '-'}</TableCell>
                    {canManage && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-card-${c.id}`}><Edit size={13} /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(c.id)} data-testid={`delete-card-${c.id}`}><Trash2 size={13} /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Crea/Modifica */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Modifica Carta' : 'Nuova Carta'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="card-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Fornitore / Circuito *</Label>
              <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="es. Eni, Q8, Telepass..." data-testid="card-provider-input" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Numero carta</Label>
              <Input value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} data-testid="card-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Assegna a</Label>
              <Select value={form.assigned_type} onValueChange={(v) => setForm({ ...form, assigned_type: v, assigned_id: '' })}>
                <SelectTrigger data-testid="card-assign-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  <SelectItem value="equipment">Un mezzo</SelectItem>
                  <SelectItem value="employee">Una persona</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>&nbsp;</Label>
              {form.assigned_type === 'equipment' ? (
                <Select value={form.assigned_id} onValueChange={(v) => setForm({ ...form, assigned_id: v })}>
                  <SelectTrigger data-testid="card-assign-equipment"><SelectValue placeholder="Seleziona mezzo..." /></SelectTrigger>
                  <SelectContent>
                    {equipment.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.license_plate ? ` (${e.license_plate})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : form.assigned_type === 'employee' ? (
                <Select value={form.assigned_id} onValueChange={(v) => setForm({ ...form, assigned_id: v })}>
                  <SelectTrigger data-testid="card-assign-employee"><SelectValue placeholder="Seleziona persona..." /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : <div className="h-10" />}
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Responsabile (giustifica le spese di questa carta)</Label>
              <Select value={form.responsible_id} onValueChange={(v) => setForm({ ...form, responsible_id: v })}>
                <SelectTrigger data-testid="card-responsible"><SelectValue placeholder="Nessuno" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuno</SelectItem>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">Indipendente dal mezzo: puoi assegnare la carta a un veicolo e avere comunque una persona responsabile che registra le spese.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Plafond (EUR)</Label>
              <Input type="number" step="0.01" min="0" value={form.plafond} onChange={(e) => setForm({ ...form, plafond: e.target.value })} placeholder="es. 500" data-testid="card-plafond-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Scadenza</Label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} id="card-active" />
              <Label htmlFor="card-active" className="font-normal">Carta attiva</Label>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.provider.trim()} data-testid="card-save-btn">
              {editItem ? 'Salva Modifiche' : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
